import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { keyframes } from '@mui/system';

// Custom hooks and components
import useDataCatalogState from './data_catalog/hooks/useDataCatalogState';
import useFilterSort from './data_catalog/hooks/useFilterSort';
import useWorkingLists from './data_catalog/hooks/useWorkingLists';
import DataCatalogTable from './data_catalog/components/DataCatalogTable';
import CompactFilterBar from './data_catalog/components/CompactFilterBar';
import FilterExpansionPanel from './data_catalog/components/FilterExpansionPanel';
import WorkingListDialog from './data_catalog/components/WorkingListDialog';
import ListAddRequestDialog from './data_catalog/components/ListAddRequestDialog';
import ListManagementDialog from './data_catalog/components/ListManagementDialog';
import AIAssistantDialog from './data_catalog/components/AIAssistantDialog';
import TagManagementDialog from './data_catalog/components/TagManagementDialog';
import SetManagementDialog from './data_catalog/components/SetManagementDialog';
import TagDeletionRequestDialog from './data_catalog/components/TagDeletionRequestDialog';
import { getOMOPStatusColor, getOMOPStatusLabel, getPrimaryName, tagIconOptions as importedTagIconOptions, getIconComponent as importedGetIconComponent } from './data_catalog/utils/helpers';
import DataCatalogHelpSection from '../../components/olymp/DataCatalogHelpSection';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/authContext';

// UI-only fixture data
import aiSuggestionsData from '../../data/aiSuggestions.json';

// Catalog API service
import { getParameters, getCatalogTags, getCatalogStations, getSavedSets } from '../../services/catalogService';

// Subtle pulse animation for tags with deletion requests
const subtlePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(237, 108, 2, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0);
  }
`;

// Use imported versions from helpers.js
const tagIconOptions = importedTagIconOptions;
const getIconComponent = importedGetIconComponent;

function resolveCatalogStatusMessage(t, ...errors) {
  return errors.some(error => error?.status === 401)
    ? t('dataCatalog.status.authRequired')
    : t('dataCatalog.status.apiUnavailable');
}

const DataCatalog = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const currentUser = user?.name || user?.email || '';
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);
  const [selectionHydrated, setSelectionHydrated] = useState(false);

  // Use custom hooks for state management
  const state = useDataCatalogState();
  const workingLists = useWorkingLists();

  // Destructure state for easier access
  const {
    searchTerm, setSearchTerm,
    nameView, setNameView,
    showFilters, setShowFilters,
    expandedRows, setExpandedRows,
    selectedTags, setSelectedTags,
    selectedStations, setSelectedStations,
    selectedSensitivity, setSelectedSensitivity,
    selectedOmopStatus, setSelectedOmopStatus,
    selectedSourceSystems, setSelectedSourceSystems,
    filterPanelOpen, setFilterPanelOpen,
    orderBy, setOrderBy,
    order, setOrder,
    tagDialogOpen, setTagDialogOpen,
    editingParameterId, setEditingParameterId,
    newTagInput, setNewTagInput,
    listDialogOpen, setListDialogOpen,
    selectedParameterForList, setSelectedParameterForList,
    listManagementDialogOpen, setListManagementDialogOpen,
    setManagementDialogOpen, setSetManagementDialogOpen,
    activeSetTab, setActiveSetTab,
    createTagPrivate, setCreateTagPrivate,
    selectedTagIcon, setSelectedTagIcon,
    deletionRequestDialog, setDeletionRequestDialog,
    activeWorkingList, setActiveWorkingList,
    showOnlyActiveListParams, setShowOnlyActiveListParams,
    listComparisonFilter, setListComparisonFilter,
    listAddRequestDialog, setListAddRequestDialog,
    aiAssistantOpen, setAiAssistantOpen,
    studyDescription, setStudyDescription,
    studyType, setStudyType,
    specialty, setSpecialty,
    aiSuggestions, setAiSuggestions,
    selectedSuggestions, setSelectedSuggestions,
    isGeneratingSuggestions, setIsGeneratingSuggestions,
    aiSuggestedParameterIds, setAiSuggestedParameterIds,
    setParameterIds, setSetParameterIds,
    snackbar, setSnackbar,
    favorites, setFavorites,
    handleRowClick,
    isRowExpanded,
    handleTagToggle,
    handleStationToggle,
    handleSourceSystemToggle,
    clearAllFilters,
    toggleFavorite,
  } = state;

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Destructure working list functions
  const {
    parameterLists,
    error: workingListsError,
    selectionLoading,
    workingListSelection,
    persistWorkingListSelection,
    isParameterInAnyList,
    isParameterInActiveList,
    hasParameterPendingInActiveList,
    isParameterInList,
    hasParameterPendingApproval,
    handleAddToList: addToList,
    handleRemoveFromList: removeFromList,
    handleToggleParameterInList,
    createList,
    renameList,
    deleteList,
    duplicateList,
  } = workingLists;

  // ── API-driven data state ───────────────────────────────────────────────
  const [catalogParameters, setCatalogParameters] = useState([]);
  const [catalogDataSource, setCatalogDataSource] = useState('idle');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [savedSets, setSavedSets] = useState([]);

  const [apiTags, setApiTags] = useState([]);
  const [apiStations, setApiStations] = useState([]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    async function loadSavedSets() {
      const result = await getSavedSets();
      if (cancelled) return;

      if (result.source === 'api') {
        setSavedSets(result.sets);
      } else {
        setSavedSets([]);
      }
    }

    loadSavedSets();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const catalogFilters = {
      q: debouncedSearchTerm,
      tags: selectedTags,
      stations: selectedStations,
      sensitivity: selectedSensitivity && selectedSensitivity !== 'all' ? selectedSensitivity : '',
      omopStatus: selectedOmopStatus && selectedOmopStatus !== 'all' ? selectedOmopStatus : '',
      sourceSystem: selectedSourceSystems,
    };

    async function loadCatalogData() {
      setCatalogLoading(true);
      setCatalogError(null);
      const [paramResult, tagResult, stationResult] = await Promise.all([
        getParameters(catalogFilters),
        getCatalogTags(catalogFilters),
        getCatalogStations(catalogFilters),
      ]);

      if (cancelled) return;

      setCatalogParameters(paramResult.parameters);
      setCatalogDataSource(paramResult.source);
      setApiTags(tagResult.source === 'api' ? tagResult.tags : []);
      setApiStations(stationResult.source === 'api' ? stationResult.stations : []);

      if (paramResult.source !== 'api') {
        setCatalogError(resolveCatalogStatusMessage(t, paramResult.error, tagResult.error, stationResult.error));
      } else if (tagResult.source !== 'api' || stationResult.source !== 'api') {
        setCatalogError(
          tagResult.error?.status === 401 || stationResult.error?.status === 401
            ? t('dataCatalog.status.authRequired')
            : t('dataCatalog.status.filtersPartiallyAvailable')
        );
      }

      setCatalogLoading(false);
    }
    loadCatalogData();
    return () => { cancelled = true; };
  }, [
    debouncedSearchTerm,
    selectedOmopStatus,
    selectedSensitivity,
    selectedSourceSystems,
    selectedStations,
    selectedTags,
    t,
  ]);

  const catalogItems = catalogParameters;

  // Tag data
  const allTagsWithStats = apiTags.map(tag => ({ name: tag.value, count: tag.count || 0 }));
  const allTags = apiTags.map(t => t.value);
  const popularTags = apiTags.slice(0, 10).map(t => t.value);

  // Station data
  const allStations = apiStations.map(s => s.value);
  const allStationsWithCategories = allStations.map(station => ({
    name: station,
    category: 'Andere',
    building: '',
  }));
  const popularStations = apiStations.slice(0, 10).map(s => s.value);

  // Source systems — extracted from loaded parameters
  const allSourceSystems = [...new Set(catalogItems.map(p => p.sourceSystem).filter(Boolean))];

  // parameterLists state is now managed by useWorkingLists hook
  // Use custom hook for filtering and sorting
  const {
    filteredParameters,
    sortedParameters,
    getParameterListStatus,
    getListComparisonCounts,
  } = useFilterSort({
    parameters: catalogItems,
    applyCatalogFiltersLocally: false,
    searchTerm,
    selectedTags,
    selectedStations,
    selectedSensitivity,
    selectedOmopStatus,
    selectedSourceSystems,
    aiSuggestedParameterIds,
    setParameterIds,
    showOnlyActiveListParams,
    activeWorkingList,
    listComparisonFilter,
    orderBy,
    order,
    parameterLists,
    isParameterInActiveList,
  });

  // Enrich parameters with favorite status
  const parametersWithFavorites = sortedParameters.map(param => ({
    ...param,
    isFavorite: favorites.includes(param.id)
  }));

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // handleTagToggle is already provided by useDataCatalogState hook

  const handleFavoriteToggle = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click
    toggleFavorite(parameterId);
  };

  // List Management Handlers
  const handleOpenListDialog = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click

    // Quick-add: If exactly one active working list is set, add directly without dialog
    if (parameterLists && activeWorkingList.length === 1) {
      const list = parameterLists.find(l => l.id === activeWorkingList[0]);
      if (list) {
        const parameter = catalogItems.find(p => p.id === parameterId);
        const isAlreadyInList = list.parameterIds.includes(parameterId);
        const hasPendingRequest = list.additionRequests.some(req => req.parameterId === parameterId);

        if (isAlreadyInList) {
          // Show message that it's already in the list
          setSnackbar({
            open: true,
            message: t('dataCatalog.messages.alreadyInList', { parameter: parameter?.name, list: list.name }),
            severity: 'info'
          });
          return;
        }

        if (hasPendingRequest) {
          // Show message that request is already pending
          setSnackbar({
            open: true,
            message: t('dataCatalog.messages.pendingSuggestion', { parameter: parameter?.name, list: list.name }),
            severity: 'info'
          });
          return;
        }

        addToList(parameterId, list.id, parameter?.name || '').then((result) => {
          setSnackbar({
            open: true,
            message: result.message,
            severity: result.severity || (result.info ? 'info' : 'success')
          });
        });
      }
      return;
    }

    // Normal behavior: Open dialog
    setSelectedParameterForList(parameterId);
    setListDialogOpen(true);
  };

  const handleRemoveFromList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = catalogItems.find(p => p.id === parameterId);

    if (list && parameter) {
      removeFromList(parameterId, listId, parameter.name).then((result) => {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || (result.info ? 'info' : 'success')
        });
      });
    }
  };

  const handleAddToList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = catalogItems.find(p => p.id === parameterId);

    if (list && parameter) {
      addToList(parameterId, listId, parameter.name).then((result) => {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || (result.info ? 'info' : 'success')
        });
      });
    }
  };

  // Working list functions are now provided by useWorkingLists hook
  // List comparison and filter functions are now provided by useFilterSort hook

  const handleConfirmListAdditionRequest = () => {
    const { list, parameter } = listAddRequestDialog;
    if (!list || !parameter) return;

    setListAddRequestDialog({ open: false, list: null, parameter: null });
  };

  useEffect(() => {
    if (!workingListsError) {
      return;
    }

    setSnackbar({
      open: true,
      message: resolveCatalogStatusMessage(t, workingListsError),
      severity: 'error'
    });
  }, [t, workingListsError, setSnackbar]);

  useEffect(() => {
    if (selectionLoading || selectionHydrated) {
      return;
    }

    setActiveWorkingList(workingListSelection.activeListIds);
    setShowOnlyActiveListParams(workingListSelection.showOnlyActiveListParams);
    setListComparisonFilter(workingListSelection.listComparisonFilter);
    setSelectionHydrated(true);
  }, [
    selectionHydrated,
    selectionLoading,
    setActiveWorkingList,
    setListComparisonFilter,
    setShowOnlyActiveListParams,
    workingListSelection,
  ]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }

    let cancelled = false;

    async function persistSelection() {
      try {
        const nextSelection = await persistWorkingListSelection({
          activeListIds: activeWorkingList,
          showOnlyActiveListParams,
          listComparisonFilter,
        });

        if (cancelled) {
          return;
        }

        if (JSON.stringify(nextSelection.activeListIds) !== JSON.stringify(activeWorkingList)) {
          setActiveWorkingList(nextSelection.activeListIds);
        }
        if (nextSelection.showOnlyActiveListParams !== showOnlyActiveListParams) {
          setShowOnlyActiveListParams(nextSelection.showOnlyActiveListParams);
        }
        if (nextSelection.listComparisonFilter !== listComparisonFilter) {
          setListComparisonFilter(nextSelection.listComparisonFilter);
        }
      } catch (persistError) {
        if (cancelled) {
          return;
        }

        setSnackbar({
          open: true,
          message: persistError.message || resolveCatalogStatusMessage(t, persistError),
          severity: 'error',
        });
      }
    }

    persistSelection();

    return () => {
      cancelled = true;
    };
  }, [
    activeWorkingList,
    listComparisonFilter,
    persistWorkingListSelection,
    selectionHydrated,
    setActiveWorkingList,
    setListComparisonFilter,
    setShowOnlyActiveListParams,
    setSnackbar,
    showOnlyActiveListParams,
    t,
  ]);

  useEffect(() => {
    setActiveWorkingList(prevActiveLists =>
      prevActiveLists.filter(listId => parameterLists.some(list => list.id === listId))
    );
  }, [parameterLists, setActiveWorkingList]);

  // handleRowClick and isRowExpanded are now provided by useDataCatalogState hook

  const handleAddTagToParameter = (parameterId, tagName, isPrivate = createTagPrivate, icon = null) => {
    // Check if tag exists
    const existingTag = allTagsWithStats.find(t => t.name === tagName);
    if (!existingTag) {
      // In real app: API call to create tag with privacy setting and icon
    }
    // Mock: This would update the parameter's tags in state
  };

  const handleRemoveTagFromParameter = (parameterId, tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);

    if (!tag) {
      return;
    }

    // If private tag and user is owner, allow direct deletion
    if (tag.isPrivate && tag.createdBy === currentUser) {
      return;
    }

    // If public tag, require approval process
    if (!tag.isPrivate) {
      setDeletionRequestDialog({ open: true, tag, parameterId });
      return;
    }

    // If trying to remove someone else's private tag (shouldn't happen in UI)
  };

  const handleCreateNewTag = (tagName, isPrivate = createTagPrivate) => {
    // In real app, this would call API to create tag
  };

  const handleMakeTagPublic = (tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);
    if (tag && tag.isPrivate && tag.createdBy === currentUser) {
    }
  };

  const handleRequestTagDeletion = (tagName, parameterId) => {
    // In real app: API call to add deletion request
    // If second user confirms, tag gets removed
  };

  const handleConfirmDeletionRequest = () => {
    const { tag, parameterId } = deletionRequestDialog;
    if (!tag) return;

    setDeletionRequestDialog({ open: false, tag: null });
  };

  const handleLoadSet = (set) => {
    // Load tags if present
    if (set.items.tags && set.items.tags.length > 0) {
      setSelectedTags(prev => {
        const newTags = set.items.tags.filter(tag => !prev.includes(tag));
        return [...prev, ...newTags];
      });
    }

    // Load stations if present
    if (set.items.stations && set.items.stations.length > 0) {
      setSelectedStations(prev => {
        const newStations = set.items.stations.filter(station => !prev.includes(station));
        return [...prev, ...newStations];
      });
    }

    // Load parameters if present - use setParameterIds for saved sets
    if (set.items.parameterIds && set.items.parameterIds.length > 0) {
      setSetParameterIds(prev => {
        const newIds = set.items.parameterIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }

    // Show success message
    setSnackbar({
      open: true,
      message: t('dataCatalog.status.setApplied', { name: set.name }),
      severity: 'success'
    });
  };

  const getSetTypeBadge = (type) => {
    switch (type) {
      case 'parameters': return 'PARAM';
      case 'stations': return 'STAT';
      case 'tags': return 'TAG';
      case 'mixed': return 'MIX';
      default: return 'SET';
    }
  };

  const getOMOPStatusColor = (status) => {
    switch (status) {
      case 'validated': return 'success';
      case 'mapped': return 'info';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // AI Assistant Handlers
  const handleGenerateSuggestions = () => {
    setIsGeneratingSuggestions(true);

    // Simulate AI processing
    setTimeout(() => {
      // Use AI suggestions from JSON data
      setAiSuggestions(aiSuggestionsData);
      // Pre-select all suggestions
      setSelectedSuggestions(aiSuggestionsData.map(s => s.id));
      setIsGeneratingSuggestions(false);
    }, 2000);
  };

  const handleApplySuggestionsAsFilter = () => {
    // Only use selected suggestions, not all
    const parameterIds = selectedSuggestions;

    // Set as filter
    setAiSuggestedParameterIds(parameterIds);

    // Show success message
    setSnackbar({
      open: true,
      message: t('dataCatalog.messages.aiFiltersApplied', { count: parameterIds.length }),
      severity: 'success'
    });

    // Close dialog and reset
    setAiAssistantOpen(false);
    setStudyDescription('');
    setStudyType('');
    setSpecialty('');
    setAiSuggestions([]);
    setSelectedSuggestions([]);
  };

  const getOMOPStatusLabel = (status) => {
    switch (status) {
      case 'validated': return 'Validiert';
      case 'mapped': return 'Gemappt';
      case 'pending': return 'Ausstehend';
      default: return 'Unbekannt';
    }
  };

  const getPrimaryName = (param) => {
    if (nameView === 'omop') return param.omopName || param.name;
    return param.name;
  };

  const getSecondaryName = (param) => {
    if (nameView === 'both') {
      if (nameView === 'omop') return param.name;
      return param.omopName;
    }
    return null;
  };

  const getPrimaryDescription = (param) => {
    if (nameView === 'omop') return param.omopDescription || param.description;
    return param.description;
  };

  // Utility function to get parameter name by ID
  const getParameterName = (paramId) => {
    const param = catalogItems.find(p => p.id === paramId);
    return param ? param.name : `Parameter ${paramId}`;
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>

        {/* API status banners */}
        {catalogLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              {t('dataCatalog.status.loading')}
            </Typography>
          </Box>
        )}
        {!catalogLoading && catalogError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCatalogError(null)}>
            {catalogError}
          </Alert>
        )}
        {!catalogLoading && catalogDataSource === 'api' && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCatalogDataSource('acknowledged')}>
            {t('dataCatalog.status.loadedFromApi', { count: catalogParameters.length })}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 0 }}>
              {t('dataCatalog.title')}
            </Typography>
            <Tooltip title={t('dataCatalog.overviewTooltip')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('dataCatalog.overviewAria')}
                onClick={() => setOverviewDialogOpen(true)}
                sx={{ mb: 0.5 }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1" color="textSecondary">
            {t('dataCatalog.resultCount', { count: filteredParameters.length })}
          </Typography>
        </Box>

        <DataCatalogHelpSection />

        {/* Compact Filter Bar with integrated search */}
        <CompactFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
          selectedTags={selectedTags}
          selectedStations={selectedStations}
          selectedSensitivity={selectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          selectedSourceSystems={selectedSourceSystems}
          aiSuggestedParameterIds={aiSuggestedParameterIds}
          setParameterIds={setParameterIds}
          setSelectedTags={setSelectedTags}
          setSelectedStations={setSelectedStations}
          setSelectedSensitivity={setSelectedSensitivity}
          setSelectedOmopStatus={setSelectedOmopStatus}
          setSelectedSourceSystems={setSelectedSourceSystems}
          setAiSuggestedParameterIds={setAiSuggestedParameterIds}
          setSetParameterIds={setSetParameterIds}
          parameters={catalogItems}
          getParameterName={getParameterName}
          filterPanelOpen={filterPanelOpen}
          setFilterPanelOpen={setFilterPanelOpen}
          setAiAssistantOpen={setAiAssistantOpen}
          nameView={nameView}
          setNameView={setNameView}
          clearAllFilters={clearAllFilters}
        />

        {/* Filter Expansion Panel */}
        <FilterExpansionPanel
          open={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          selectedStations={selectedStations}
          setSelectedStations={setSelectedStations}
          selectedSensitivity={selectedSensitivity}
          setSelectedSensitivity={setSelectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          setSelectedOmopStatus={setSelectedOmopStatus}
          selectedSourceSystems={selectedSourceSystems}
          setSelectedSourceSystems={setSelectedSourceSystems}
          allSourceSystems={allSourceSystems}
          handleSourceSystemToggle={handleSourceSystemToggle}
          allTags={allTags}
          popularTags={popularTags}
          allStations={allStations}
          allStationsWithCategories={allStationsWithCategories}
          popularStations={popularStations}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          handleTagToggle={handleTagToggle}
          setTagDialogOpen={setTagDialogOpen}
        />

        {/* Active Working List Selector */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: activeWorkingList.length > 0 ? '#f0f7ff' : 'background.paper', border: activeWorkingList.length > 0 ? '2px solid' : 'none', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('dataCatalog.parameterListsTitle')}
            </Typography>
            <Tooltip
              title={t('dataCatalog.parameterListsTooltip')}
              arrow
            >
              <InfoOutlinedIcon fontSize="small" color="primary" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
              {activeWorkingList.length > 0 ? (
                <PlaylistAddCheckIcon color="primary" />
              ) : (
                <PlaylistAddIcon color="action" />
              )}
              <Typography variant="subtitle2" fontWeight="bold" color={activeWorkingList.length > 0 ? 'primary' : 'textSecondary'}>
                {activeWorkingList.length === 2
                  ? t('dataCatalog.activeWorkingListMany')
                  : t('dataCatalog.activeWorkingListOne')}
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 250, flexGrow: 1 }}>
              <Select
                multiple
                value={activeWorkingList}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to max 2 selections
                  if (Array.isArray(value) && value.length <= 2) {
                    setActiveWorkingList(value);
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <em style={{ color: '#999' }}>{t('dataCatalog.noListSelected')}</em>;
                  }
                  const selectedLists = parameterLists ? selected.map(id => parameterLists.find(l => l.id === id)).filter(Boolean) : [];
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {selectedLists.map((list, idx) => (
                        <Box key={list.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: idx === 0 ? '#e3f2fd' : '#e8f5e9', px: 1, py: 0.5, borderRadius: 1 }}>
                          {selectedLists.length >= 1 && (
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: idx === 0 ? '#2196f3' : '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: 'bold', mr: 0.5 }}>
                              {idx === 0 ? 'A' : 'B'}
                            </Box>
                          )}
                          {list.isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                          <Typography variant="body2" fontWeight="bold">{list.name}</Typography>
                          <Chip label={`${list.parameterIds.length}`} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                        </Box>
                      ))}
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled sx={{ opacity: 1, fontStyle: 'italic', fontSize: '0.75rem', color: '#999', py: 1 }}>
                  {activeWorkingList.length === 0
                    ? t('dataCatalog.selectUpToTwo')
                    : activeWorkingList.length === 1
                      ? t('dataCatalog.selectSecondList')
                      : t('dataCatalog.maxReached')}
                </MenuItem>
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5 }}>
                  {`--- ${t('dataCatalog.privateLists')} ---`}
                </MenuItem>
                {parameterLists
                  .filter(list => list.isPrivate)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🔒 {list.name} {list.isDefault && '⭐'} ({list.parameterIds.length})
                    </MenuItem>
                  ))}
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5, mt: 1 }}>
                  {`--- ${t('dataCatalog.publicLists')} ---`}
                </MenuItem>
                {parameterLists
                  .filter(list => !list.isPrivate)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🌐 {list.name} ✓ ({list.parameterIds.length})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {activeWorkingList.length > 0 && (
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setActiveWorkingList([]);
                  setShowOnlyActiveListParams(false);
                  setListComparisonFilter('all');
                }}
                variant="outlined"
              >
                {t('dataCatalog.deactivate')}
              </Button>
            )}

            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setListManagementDialogOpen(true)}
              variant="outlined"
            >
              {t('dataCatalog.manageAllLists')}
            </Button>

            {activeWorkingList.length === 1 && (
                <Chip
                  icon={showOnlyActiveListParams ? <CheckCircleIcon /> : <FilterListIcon />}
                  label={showOnlyActiveListParams ? t('dataCatalog.onlyActiveList') : t('dataCatalog.allParameters')}
                  onClick={() => setShowOnlyActiveListParams(!showOnlyActiveListParams)}
                  color={showOnlyActiveListParams ? "success" : "default"}
                variant={showOnlyActiveListParams ? "filled" : "outlined"}
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>

          {/* Filter Chips for List Comparison */}
          {activeWorkingList.length === 2 && parameterLists && (() => {
            const counts = getListComparisonCounts();
            const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
            const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

            return (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                  {t('dataCatalog.comparison')}
                </Typography>
                <Chip
                  label={t('dataCatalog.listComparison.all', { count: filteredParameters.length })}
                  onClick={() => setListComparisonFilter('all')}
                  color={listComparisonFilter === 'all' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>A</Box>}
                  label={t('dataCatalog.listComparison.onlyA', { list: listA?.name, count: counts?.list1Only || 0 })}
                  onClick={() => setListComparisonFilter('list1Only')}
                  color={listComparisonFilter === 'list1Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list1Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>B</Box>}
                  label={t('dataCatalog.listComparison.onlyB', { list: listB?.name, count: counts?.list2Only || 0 })}
                  onClick={() => setListComparisonFilter('list2Only')}
                  color={listComparisonFilter === 'list2Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list2Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  label={t('dataCatalog.listComparison.both', { count: counts?.both || 0 })}
                  onClick={() => setListComparisonFilter('both')}
                  color={listComparisonFilter === 'both' ? 'secondary' : 'default'}
                  variant={listComparisonFilter === 'both' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label={t('dataCatalog.listComparison.neither', { count: counts?.neither || 0 })}
                  onClick={() => setListComparisonFilter('neither')}
                  color={listComparisonFilter === 'neither' ? 'default' : 'default'}
                  variant={listComparisonFilter === 'neither' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            );
          })()}

          {activeWorkingList.length === 1 && parameterLists && (
            <Alert
              severity={parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate ? "info" : "warning"}
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>{t('dataCatalog.labels.listAEnabled')}</strong> {t('dataCatalog.messages.listAEnabledInfo')}{' '}
                {parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate
                  ? t('dataCatalog.messages.listAEnabledInfoDirect')
                  : t('dataCatalog.messages.listAEnabledInfoSuggest')
                }
              </Typography>
            </Alert>
          )}

          {activeWorkingList.length === 2 && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>{t('dataCatalog.labels.listComparisonEnabled')}</strong> {t('dataCatalog.messages.listComparisonInfo')}
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Results Table */}
        <DataCatalogTable
          parameters={parametersWithFavorites}
          nameView={nameView}
          expandedRows={expandedRows}
          onRowClick={handleRowClick}
          onFavoriteToggle={handleFavoriteToggle}
          handleOpenListDialog={handleOpenListDialog}
          orderBy={orderBy}
          order={order}
          handleRequestSort={handleRequestSort}
          activeWorkingList={activeWorkingList}
          parameterLists={parameterLists}
          getParameterListStatus={getParameterListStatus}
          isParameterInAnyList={isParameterInAnyList}
          handleRemoveFromList={handleRemoveFromList}
          handleAddToList={handleAddToList}
          allTagsWithStats={allTagsWithStats}
          getIconComponent={getIconComponent}
          getPrimaryDescription={getPrimaryDescription}
          subtlePulse={subtlePulse}
        />

        {/* Tag Management Dialog */}
        <TagManagementDialog
          open={tagDialogOpen}
          onClose={() => setTagDialogOpen(false)}
          allTagsWithStats={allTagsWithStats}
          currentUser={currentUser}
          getIconComponent={getIconComponent}
          handleMakeTagPublic={handleMakeTagPublic}
        />

        {/* Set Management Dialog */}
        <SetManagementDialog
          open={setManagementDialogOpen}
          onClose={() => setSetManagementDialogOpen(false)}
          savedSets={savedSets}
          activeSetTab={activeSetTab}
          setActiveSetTab={setActiveSetTab}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
        />

        {/* Tag Deletion Request Dialog */}
        <TagDeletionRequestDialog
          open={deletionRequestDialog.open}
          onClose={() => setDeletionRequestDialog({ open: false, tag: null })}
          tag={deletionRequestDialog.tag}
          onConfirm={handleConfirmDeletionRequest}
        />

        {/* Parameter List Dialog - Add parameter to lists */}
        <WorkingListDialog
          open={listDialogOpen}
          onClose={() => setListDialogOpen(false)}
          selectedParameterId={selectedParameterForList}
          selectedParameter={catalogItems.find(p => p.id === selectedParameterForList)}
          parameterLists={parameterLists}
          currentUser={currentUser}
          isParameterInList={isParameterInList}
          handleToggleParameterInList={handleToggleParameterInList}
          hasParameterPendingApproval={hasParameterPendingApproval}
          setSnackbar={setSnackbar}
        />

        {/* List Management Dialog - Manage all lists */}
        <ListManagementDialog
          open={listManagementDialogOpen}
          onClose={() => setListManagementDialogOpen(false)}
          parameterLists={parameterLists}
          activeWorkingList={activeWorkingList}
          setActiveWorkingList={setActiveWorkingList}
          setSnackbar={setSnackbar}
          onCreateList={createList}
          onRenameList={renameList}
          onDeleteList={deleteList}
          onDuplicateList={duplicateList}
        />

        {/* List Addition Request Confirmation Dialog */}
        <ListAddRequestDialog
          open={listAddRequestDialog.open}
          onClose={() => setListAddRequestDialog({ open: false, list: null, parameter: null })}
          list={listAddRequestDialog.list}
          parameterId={listAddRequestDialog.parameter}
          onConfirm={handleConfirmListAdditionRequest}
        />

        {/* AI Assistant Modal */}
        <AIAssistantDialog
          open={aiAssistantOpen}
          onClose={() => setAiAssistantOpen(false)}
          studyDescription={studyDescription}
          setStudyDescription={setStudyDescription}
          studyType={studyType}
          setStudyType={setStudyType}
          specialty={specialty}
          setSpecialty={setSpecialty}
          aiSuggestions={aiSuggestions}
          setAiSuggestions={setAiSuggestions}
          selectedSuggestions={selectedSuggestions}
          setSelectedSuggestions={setSelectedSuggestions}
          handleGenerateSuggestions={handleGenerateSuggestions}
          handleApplySuggestionsAsFilter={handleApplySuggestionsAsFilter}
          isGeneratingSuggestions={isGeneratingSuggestions}
        />

        {/* Toast Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={(event, reason) => {
            // Ignore clickaway - only close on timeout or manual close
            if (reason === 'clickaway') {
              return;
            }
            setSnackbar({ ...snackbar, open: false });
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={overviewDialogOpen} onClose={() => setOverviewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('dataCatalog.overviewDialogTitle')}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('dataCatalog.overviewDialogDescription')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('dataCatalog.overviewDialogListATitle')}</strong> {t('dataCatalog.overviewDialogListADescription')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{t('dataCatalog.overviewDialogListBTitle')}</strong> {t('dataCatalog.overviewDialogListBDescription')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dataCatalog.overviewDialogWorkflow')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOverviewDialogOpen(false)}>
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default DataCatalog;
